import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

const formatPrice = (n) => `$${Number(n).toLocaleString('en-AU')}`;

const PriceBlock = ({ regular, founding, color }) => (
  <div className="mb-6">
    {regular && (
      <p className="text-sm text-[#8B7355]/60 uppercase tracking-widest font-serif">
        Regular price <span className="line-through">{formatPrice(regular)}</span>
      </p>
    )}
    <div className="mt-1">
      <span className="text-xs text-[#C9A961] uppercase tracking-widest font-serif block">Founding Price</span>
      <span className={`text-4xl font-bold ${color}`}>{formatPrice(founding)}</span>
    </div>
  </div>
);

const PackageCard = ({
  title,
  regularPrice,
  foundingPrice,
  priceColor,
  tagline,
  badge,
  badgeBg,
  borderClass,
  bulletColor,
  introText,
  bullets,
  buttonClass,
  buttonVariant,
  onReserve,
}) => (
  <Card className={`${borderClass} bg-white shadow-2xl transform hover:scale-105 transition-all duration-300 relative overflow-hidden`}>
    {badge && (
      <div className={`absolute top-0 right-0 ${badgeBg} text-white px-6 py-2 font-serif text-sm`}>
        {badge}
      </div>
    )}
    <CardContent className={`p-8 ${badge ? 'pt-12' : ''}`}>
      <h3 className="text-3xl font-serif text-[#8B7355] mb-4">{title}</h3>
      <PriceBlock regular={regularPrice} founding={foundingPrice} color={priceColor} />
      <p className="text-sm text-[#8B7355]/70 mb-6 italic">{tagline}</p>

      <div className="space-y-3 mb-8">
        {introText && <p className="text-[#8B7355] font-semibold">{introText}</p>}
        {bullets.map((b) => (
          <div key={b} className="flex items-start gap-2">
            <div className={`w-2 h-2 ${bulletColor} rounded-full mt-2`}></div>
            <p className="text-[#8B7355]/80">{b}</p>
          </div>
        ))}
      </div>

      <Button
        onClick={onReserve}
        variant={buttonVariant}
        className={`w-full ${buttonClass} py-6 rounded-none font-serif text-lg`}
        data-testid={`reserve-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        Reserve This Package
      </Button>
    </CardContent>
  </Card>
);

// Add-ons row (matches the Founding Collection card layout)
const ADDONS = [
  { name: 'Extra Hour', price: '$450', desc: 'Extend the magic' },
  { name: 'Luxury Guest Book', price: '$200', desc: 'Premium keepsake with instant photo album' },
  { name: 'Custom Event Design', price: '$50', desc: 'Welcome screen & print template' },
  { name: 'Additional Print Copies', price: '$100', desc: 'Extra prints for the wedding party' },
  { name: 'USB of High-Res Images', price: '$50', desc: 'All raw photos on a branded USB stick' },
  { name: 'Idle Time', price: '$150/hr', desc: 'Between ceremony & reception' },
];

// Package bullets aligned with the founding-collection card
const CLASSIC_BULLETS = [
  'Unlimited photo sessions',
  'Instant premium prints',
  'Digital gallery',
  'Professional booth attendant',
  'Choice of print design',
  'Standard prop collection',
];
const SIGNATURE_BULLETS = [
  'Luxury guest book',
  'Premium prop collection',
  'Custom event design (welcome screen & print)',
  'Priority setup',
];
const GRAND_BULLETS = [
  'Additional print copies',
  'Premium guest book',
  'VIP service',
  'Complete custom experience',
];

export const PackagesSection = ({ scrollToBooking }) => (
  <section id="packages" className="py-24 bg-[#F5F1E8]">
    <div className="max-w-7xl mx-auto px-6">
      {/* Founding Collection banner */}
      <div className="text-center mb-4">
        <span className="inline-block px-6 py-1 border-t border-b border-[#C9A961]/70 text-[#C9A961] uppercase tracking-[0.3em] text-xs font-serif">
          Founding Collection Pricing
        </span>
        <h3 className="mt-4 text-3xl md:text-4xl font-serif text-[#8B7355] italic">
          10&#37; Off All Packages
        </h3>
      </div>

      <div className="text-center mb-16">
        <h2 className="text-5xl md:text-6xl font-serif text-[#8B7355] mb-6 italic">Choose Your Experience</h2>
        <p className="text-xl text-[#8B7355]/80 max-w-3xl mx-auto">
          Each package designed to elevate your event. Pricing varies by distance and custom requests.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-8 mb-16">
        <PackageCard
          title="The Black Tie Experience"
          regularPrice={2490}
          foundingPrice={2241}
          priceColor="text-[#C9A961]"
          tagline="4 hours of full-feature luxury — an event your guests will talk about all night"
          badge="🥇 Most Luxurious"
          badgeBg="bg-[#C9A961]"
          borderClass="border-4 border-[#C9A961]"
          bulletColor="bg-[#C9A961]"
          introText="Everything in Signature, PLUS:"
          bullets={GRAND_BULLETS}
          buttonClass="bg-[#C9A961] hover:bg-[#B89551] text-white"
          onReserve={scrollToBooking}
        />
        <PackageCard
          title="The Signature"
          regularPrice={1890}
          foundingPrice={1701}
          priceColor="text-[#8B7355]"
          tagline="3 hours — perfect for couples wanting something truly special"
          badge="🥈 Most Popular"
          badgeBg="bg-[#8B7355]"
          borderClass="border-4 border-[#8B7355]"
          bulletColor="bg-[#8B7355]"
          introText="Everything in Classic, PLUS:"
          bullets={SIGNATURE_BULLETS}
          buttonClass="bg-[#8B7355] hover:bg-[#6B5845] text-white"
          onReserve={scrollToBooking}
        />
        <PackageCard
          title="The Classic"
          regularPrice={1200}
          foundingPrice={1080}
          priceColor="text-[#8B7355]"
          tagline="2 hours of vintage magic"
          badge={null}
          borderClass="border-2 border-[#8B7355]/30"
          bulletColor="bg-[#8B7355]"
          bullets={CLASSIC_BULLETS}
          buttonClass="border-2 border-[#8B7355] text-[#8B7355] hover:bg-[#8B7355] hover:text-white"
          buttonVariant="outline"
          onReserve={scrollToBooking}
        />
      </div>

      <div className="bg-white p-12 rounded-lg shadow-xl">
        <h3 className="text-4xl font-serif text-[#8B7355] mb-8 text-center italic">Enhance Your Experience</h3>
        <div className="grid md:grid-cols-3 gap-6">
          {ADDONS.map((a) => (
            <div key={a.name} className="border-l-4 border-[#C9A961] pl-6">
              <h4 className="text-xl font-serif text-[#8B7355] mb-2">{a.name}</h4>
              <p className="text-2xl font-bold text-[#C9A961] mb-2">{a.price}</p>
              <p className="text-sm text-[#8B7355]/70">{a.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="text-center mt-8 text-[#8B7355]/70 space-y-2">
        <p className="text-sm italic">* Travel outside Brisbane metro quoted on enquiry</p>
        <p className="text-sm">
          Submit your enquiry and we&apos;ll call you within 24 hours to confirm your date and arrange payment.
        </p>
      </div>
    </div>
  </section>
);
